import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Zap, Sparkles, Flame, ImageOff } from "lucide-react";
import ProductRail from "@/components/product/ProductRail";
import { getCategories, getProducts } from "@/services/catalog";
import { useI18n } from "@/i18n/I18nProvider";
import { useApp } from "@/context/AppContext";
import { HERO_IMAGE } from "@/assets/media";

function useCountdown() {
  const [left, setLeft] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(24, 0, 0, 0);
      const diff = Math.max(0, end - now);
      setLeft({
        h: Math.floor(diff / 3.6e6),
        m: Math.floor((diff % 3.6e6) / 6e4),
        s: Math.floor((diff % 6e4) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return left;
}

function Hero() {
  const { t } = useI18n();
  return (
    <section className="mx-auto max-w-7xl px-4 pt-6">
      <div className="relative overflow-hidden rounded-3xl bg-ink text-white">
        <div className="grid items-center gap-6 md:grid-cols-2">
          <div className="relative z-10 p-8 sm:p-12 lg:p-16">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand/20 px-3 py-1 text-xs font-semibold text-brand">
              <Sparkles size={14} /> KHA · 2026
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              {t("hero_title")}
            </h1>
            <p className="mt-4 max-w-md text-white/70">{t("hero_subtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-brand-foreground transition hover:brightness-110" data-testid="hero-shop-now">
                {t("shop_now")} <ArrowRight size={16} />
              </Link>
              <Link to="/deals" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10">
                {t("explore_deals")}
              </Link>
            </div>
          </div>
          <div className="relative h-56 md:h-full md:min-h-[380px]">
            <img src={HERO_IMAGE} alt="KHA" className="absolute inset-0 h-full w-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent md:bg-gradient-to-r" />
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryTiles() {
  const { t } = useI18n();
  const { data: categories = [], isLoading } = useQuery({ queryKey: ["categories"], queryFn: getCategories });
  return (
    <section className="mx-auto max-w-7xl px-4 pt-10">
      <h2 className="mb-4 font-display text-xl font-bold text-ink sm:text-2xl">{t("featured_categories")}</h2>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {(isLoading ? Array.from({ length: 6 }) : categories).map((c, i) => (
          <Link key={c?.id || i} to={c ? `/category/${c.slug}` : "#"} className="group flex flex-col items-center gap-2">
            <div className="aspect-square w-full overflow-hidden rounded-2xl border border-border bg-secondary hover-lift">
              {c?.image ? (
                <img src={c.image} alt={c.name} className="h-full w-full object-cover transition group-hover:scale-105" />
              ) : (
                <div className="grid h-full w-full place-items-center text-muted-foreground"><ImageOff size={22} /></div>
              )}
            </div>
            <span className="text-center text-xs font-medium text-ink sm:text-sm">{c?.name || ""}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FlashDeals() {
  const { t } = useI18n();
  const { h, m, s } = useCountdown();
  const { data, isLoading } = useQuery({ queryKey: ["deals"], queryFn: () => getProducts({ onSale: true, perPage: 10 }) });
  const items = data?.items || [];
  if (!isLoading && items.length === 0) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <section className="mx-auto mt-10 max-w-7xl px-4">
      <div className="rounded-3xl bg-gradient-to-br from-brand/10 to-secondary p-4 sm:p-6">
        <ProductRail
          title={<span className="flex items-center gap-2"><Flame className="text-brand" size={22} /> {t("flash_deals")}</span>}
          products={items}
          loading={isLoading}
          viewAllTo="/deals"
          extra={
            <div className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs font-bold text-white">
              {t("ends_in")} <span className="tabular-nums">{pad(h)}:{pad(m)}:{pad(s)}</span>
            </div>
          }
        />
      </div>
    </section>
  );
}

export default function HomePage() {
  const { t } = useI18n();
  const { recentlyViewed } = useApp();
  const best = useQuery({ queryKey: ["best"], queryFn: () => getProducts({ featured: true, perPage: 10 }) });
  const trending = useQuery({ queryKey: ["trending"], queryFn: () => getProducts({ sort: "rating", perPage: 10 }) });
  const fresh = useQuery({ queryKey: ["fresh"], queryFn: () => getProducts({ sort: "newest", perPage: 10 }) });

  return (
    <div className="pb-8">
      <Hero />
      <CategoryTiles />
      <FlashDeals />
      <div className="mx-auto max-w-7xl px-4">
        <ProductRail title={t("best_sellers")} products={best.data?.items || []} loading={best.isLoading} viewAllTo="/shop" accent />
        <ProductRail title={t("trending")} products={trending.data?.items || []} loading={trending.isLoading} viewAllTo="/shop?sort=rating" accent />

        <section className="my-8 grid gap-4 sm:grid-cols-2">
          <Link to="/deals" className="group relative overflow-hidden rounded-3xl bg-ink p-8 text-white">
            <Zap className="text-brand" size={26} />
            <h3 className="mt-3 font-display text-2xl font-bold">{t("flash_deals")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("explore_deals")}</p>
            <ArrowRight className="mt-6 transition group-hover:translate-x-1" />
          </Link>
          <Link to="/shop?sort=newest" className="group relative overflow-hidden rounded-3xl border border-border bg-secondary p-8">
            <Sparkles className="text-brand" size={26} />
            <h3 className="mt-3 font-display text-2xl font-bold text-ink">{t("new_arrivals")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("shop_now")}</p>
            <ArrowRight className="mt-6 text-ink transition group-hover:translate-x-1" />
          </Link>
        </section>

        <ProductRail title={t("recommended")} products={fresh.data?.items || []} loading={fresh.isLoading} viewAllTo="/shop" accent />
        {recentlyViewed.length > 0 && (
          <ProductRail title={t("recently_viewed")} products={recentlyViewed} />
        )}
      </div>
    </div>
  );
}
